import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";



// ✅ Register Arabic font (ensure the path is correct)
Font.register({
  family: "Noto Sans Arabic",
  src: "/fonts/NotoSansArabic.ttf",
});

// ✅ Convert HTML → plain text
const htmlToPlainText = (html) => {
  if (!html) return "";
  return html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/?[^>]+(>|$)/g, "");
};

// ✅ Split HTML by <br> tags and extract text segments
const splitByBr = (html) => {
  if (!html) return [];
  // Split by <br> tags while preserving the segments
  const segments = html.split(/<br\s*\/?>/gi);
  return segments.map(segment => {
    // Remove any remaining HTML tags and trim
    const cleanSegment = segment.replace(/<\/?[^>]+(>|$)/g, "").trim();
    return cleanSegment;
  }).filter(segment => segment.length > 0);
};

// ✅ Remove dots (used to fix unwanted dots before Arabic text)
const removeDots = (text) => text.replace(/\./g, "");

// ✅ Detect if text contains Arabic
const isArabic = (text) => /[\u0600-\u06FF]/.test(text);

// ✅ Base styles
const baseStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 30,
    fontSize: 12,
    lineHeight: 1.5,
  },
  header: {
    fontSize: 24,
    marginBottom: 20,
    textAlign: "center",
    color: "#333333",
    fontWeight: "bold",
  },
  content: {
    fontSize: 14,
    marginBottom: 15,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    color: "#666666",
    fontSize: 10,
  },
});

const PDFGenerator = ({ summary, title, pdfHeaderText, pdfFooterText }) => {
  // Split content by <br> tags into segments
  const segments = splitByBr(summary || "");
  
  // Check if any segment contains Arabic (for header/footer styling)
  const hasArabic = segments.some(segment => isArabic(segment));

  // ✅ Dynamic styles
  const dynamicHeaderStyle = {
    ...baseStyles.header,
    fontFamily: hasArabic ? "Noto Sans Arabic" : "Helvetica-Bold",
    textAlign: "center",
  };

  return (
    <Document>
      <Page
        size="A4"
        style={{
          ...baseStyles.page,
          fontFamily: hasArabic ? "Noto Sans Arabic" : "Helvetica",
          direction: hasArabic ? "rtl" : "ltr",
        }}
      >
        {/* ✅ Header */}
        <View style={dynamicHeaderStyle} >
          <Text>{title || pdfHeaderText}</Text>
        </View>

        {/* ✅ Content - Render each segment with appropriate RTL/LTR */}
        <View style={baseStyles.content}>
          {segments.map((segment, index) => {
            const segmentIsArabic = isArabic(segment);
            let processedSegment = segment;
            
            // Remove dots from Arabic segments
            if (segmentIsArabic) {
              processedSegment = removeDots(segment);
            }

            const segmentStyle = {
              textAlign: segmentIsArabic ? "right" : "left",
              fontFamily: segmentIsArabic ? "Noto Sans Arabic" : "Helvetica",
              direction: segmentIsArabic ? "rtl" : "ltr",
              marginBottom: index < segments.length - 1 ? 8 : 0,
            };

            return (
              <Text key={index} style={segmentStyle}>
                {processedSegment}
              </Text>
            );
          })}
        </View>

        {/* ✅ Footer */}
        <View style={baseStyles.footer}>
          <Text>
            {pdfFooterText} — {new Date().toLocaleDateString("en-GB")}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PDFGenerator;
